import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { CardComponent } from 'src/app/shared/components/master-detail-layout/card/card.component';
import { MasterDetailLayoutComponent } from 'src/app/shared/components/master-detail-layout/master-detail-layout.component';
import { SidebarItemComponent } from 'src/app/shared/components/master-detail-layout/sidebar-item/sidebar-item.component';
import { FidelityRuleFormService } from '../../services/fidelity-rule-form.service';
import { FidelityDataCatalogService } from '../../services/fidelity-data-catalog.service';
import { FidelityNavigationStateService } from '../../services/fidelity-navigation-state.service';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { EntityManagerService, LoaderService } from 'src/app/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChainCampaigns, ChainRules } from 'tilby-models';
import { RulesFields, RulesFormGroups } from '../../models/fidelity-rule.model';
import { areObjectsEqual } from '../../utils/fidelity.utils';
import { restManager } from 'app/ajs-upgraded-providers';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { mobileCheck } from 'src/utilities';

@Component({
  selector: 'app-fidelity-rules',
  standalone: true,
  imports: [
    CommonModule,
    MasterDetailLayoutComponent,
    CardComponent,
    SidebarItemComponent,
    ActionBtnComponent,
    MatIconModule, MatButtonModule,
    MatFormFieldModule, ReactiveFormsModule,
    MatInputModule, MatSelectModule,
    TranslateModule, TruncatedTextDirective,
    MatSlideToggleModule
  ],
  templateUrl: './fidelity-rules.component.html',
  styleUrl: './fidelity-rules.component.scss'
})
export class FidelityRulesComponent implements OnInit, OnDestroy {

  readonly ruleFormService = inject(FidelityRuleFormService);
  private readonly fidelityDataCatalogService = inject(FidelityDataCatalogService);
  private readonly translateService = inject(TranslateService);
  private readonly fidelityNavigationStateService = inject(FidelityNavigationStateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly loaderService = inject(LoaderService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly restManagerService = inject(restManager);
  private readonly entityManagerService = inject(EntityManagerService);

  protected rulesList: ChainRules[] = [];
  protected selectedRule: ChainRules | null = null;

  protected initialData!: RulesFields;
  protected ruleForm!: RulesFormGroups;
  protected ruleFormReady = this.ruleFormService.ruleFormReady;

  protected ruleTypes = [
    { value: 'at_sale', name: this.translateService.instant('FIDELITY.SETUP.RULES.AT_SALE') },
    { value: 'every_euro', name: this.translateService.instant('FIDELITY.SETUP.RULES.EVERY_CURRENCY', { currency: TilbyCurrencyPipe.currency.symbol_native }) }
  ]

  protected isEdit = false;
  protected isMobile = mobileCheck();
  protected showDetailMobile = false;

  constructor() {
    this.rulesList = this.fidelityDataCatalogService.getDataByKey('rules') as ChainRules[];
  }

  ngOnInit(): void {
    this.fidelityNavigationStateService.register(() => this.checkChanges());
  }

  checkChanges(): boolean {
    if (!this.ruleForm) return false;
    const equal = areObjectsEqual(this.initialData, this.ruleForm.getRawValue());
    return !equal;
  }
  
  isSelected(rule: ChainRules) {
    if (this.selectedRule) {
      return rule.id === this.selectedRule.id;
    } 
    return false;
  }

  get isNotEmptyState() {
    return this.ruleFormReady() && (this.selectedRule || !this.isEdit)
  }

  async selectRule($event: any, index: number) {
    $event.stopPropagation();

    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.initRule(index);
    if (this.isMobile) {
      this.showDetailMobile = true;
    }
  }

  async goBack() {
    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.ruleForm = this.ruleFormService.createForm(this.selectedRule);
    this.showDetailMobile = false;
  }

  async checkPendingData() {
    if (this.fidelityNavigationStateService.hasUnsavedChanges()) {
      const confirmed = await this.confirmDialogService.openDialog({
        data: {
          messageLabel: 'FIDELITY.SETUP.RULES.EDIT_CANCEL_CONFIRM',
          confirmLabel: 'APPLICATION.YES',
          cancelLabel: 'APPLICATION.NO'
        }
      });

      if(!confirmed) return false;
    }
    return true;
  }

  initRule(index: number) {
    if(index < 0) return;

    // out of range (e.g. the last rule was deleted): nothing is selected and the empty state is shown
    this.selectedRule = this.rulesList[index] ?? null;
    this.ruleForm = this.ruleFormService.createForm(this.selectedRule);
    this.initialData = structuredClone(this.ruleForm.getRawValue()) as RulesFields;
    this.isEdit = true;
  }

  async addRule() {
    const canSelect = await this.checkPendingData();
    if(!canSelect) return;

    this.selectedRule = null;
    this.ruleForm = this.ruleFormService.createForm(this.selectedRule);
    this.initialData = structuredClone(this.ruleForm.getRawValue()) as RulesFields;
    this.isEdit = false;

    if (this.isMobile) {
      this.showDetailMobile = true;
    }
  }

  async deleteRule() {
    const idRule = this.selectedRule?.id;
    if(!idRule) return;

    const confirmed = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'FIDELITY.SETUP.RULES.DELETE_CONFIRM',
        confirmLabel: 'APPLICATION.YES',
        cancelLabel: 'APPLICATION.NO'
      }
    });

    if(!confirmed) return;

    this.loaderService.show();

    try {
      await this.restManagerService.deleteOne("rules", idRule);

      this._snackBar.open(this.translateService.instant('FIDELITY.SETUP.RULES.DELETE_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      await this.reloadRules();
      
      if (this.isMobile) {
        this.showDetailMobile = false;
      }
    } catch(error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.RULES.DELETE_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async saveRule() {
    if (this.ruleForm.invalid) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.RULES.SAVE_INCORRECT_DATA')
      }});
      return;
    }

    const ruleToSend = this.ruleForm.getRawValue();

    this.loaderService.show();
    const isEdit = !!ruleToSend?.id;

    try {
      let result: ChainRules;

      if (isEdit) {
        result = await this.restManagerService.put("rules", ruleToSend.id, ruleToSend);
      } else {
        result = await this.restManagerService.post("rules", ruleToSend);
      }

      this._snackBar.open(this.translateService.instant(isEdit ? 'FIDELITY.SETUP.RULES.SAVE_EDIT_OK' : 'FIDELITY.SETUP.RULES.SAVE_CREATE_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      await this.reloadRules(result.id);

      if (this.isMobile) {
        this.showDetailMobile = false;
      }
    } catch (error: any) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.RULES.SAVE_ERROR')
      }});
    } finally {
      this.loaderService.hide();
    }
  }

  async updateRules(id?: number) {
    const [
      campaigns, rules
    ] = await Promise.all([
      this.entityManagerService.campaigns.fetchCollectionOnline({}),
      this.restManagerService.getList("rules", { pagination: false })
    ]);
    this.fidelityDataCatalogService.addDataToCatalog('campaigns', campaigns as ChainCampaigns[] || []);
    this.fidelityDataCatalogService.setProcessedRules(rules);

    const updatedList = this.fidelityDataCatalogService.getDataByKey("rules");
    this.rulesList = updatedList as ChainRules[];

    const newIndex = id? this.rulesList.findIndex(c => c.id === id) : 0;
    this.initRule(Math.max(newIndex, 0));
  }

  /** Reloads the list after a change: a failure must not be reported as a failure of the change itself, which succeeded */
  private async reloadRules(id?: number) {
    try {
      await this.updateRules(id);
    } catch (error) {
      console.error('Failed to reload the rules', error);
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('FIDELITY.SETUP.LOAD_ERROR')
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.fidelityNavigationStateService.unregister();
    this.ruleFormReady.set(false);
  }
}
